import { useEffect, useRef } from "react";

import { endpoints } from "@/lib/api/endpoints";
import type { MenuItem } from "@/lib/api/types";
import { useTranslate } from "@/lib/i18n";
import { hasPermission, PERMISSIONS, useSession } from "@/modules/auth/useSession";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useLocalName } from "@/shared/localName";
import { usePrivateToast } from "@/shared/usePrivateToast";

/**
 * The dishes that went OUT since the last look (T11, D173): available on the
 * previous read, marked out by a PERSON on this one (`unavailableBecause`
 * SELF — a combo that is out only because a component is says nothing of its
 * own), and not by the person looking (`availabilityMarkedBySellerId`).
 *
 * `seen` is null on the first look, which returns nothing: what was already
 * out when the screen loaded is on the menu's own «نفد» tiles, and a toast for
 * every one of them on each page load would teach people to ignore it.
 */
export function freshlyMarkedOut(
  seen: ReadonlyMap<string, boolean> | null,
  current: readonly Pick<MenuItem, "id" | "isAvailable" | "unavailableBecause" | "availabilityMarkedBySellerId">[],
  selfId: string | null,
): string[] {
  if (seen === null) return [];
  return current
    .filter(
      (item) =>
        item.isAvailable === false &&
        seen.get(item.id) === true &&
        item.unavailableBecause !== "COMPONENT" &&
        (selfId === null || item.availabilityMarkedBySellerId !== selfId),
    )
    .map((item) => item.id);
}

/**
 * T11 / D173 — THE TILL HEARS AN 86.
 *
 * A cook marks the fish out on the kitchen's 86 screen; the person taking the
 * order learns it from a toast, not from a guest: «نفد «{item}» — أوقِفه على
 * تطبيقات التوصيل أيضًا» (the delivery apps do not read this menu). Mounted once
 * for the whole signed-in app beside `CashierRequestAlerts`, active for a
 * session holding `POS_USE` + `MENU_VIEW` — the till's own menu read, so the
 * cache is shared and it adds no request while a bill is open; it refreshes on
 * the menu's revision signal and on focus like every other menu reader.
 *
 * Deliberately quiet: NO chime (a cook's 86 is not a call to act this second)
 * and NO new push type (the app accepts exactly six). The person who marked it
 * is not told what they just did.
 */
export function MenuAvailabilityAlerts() {
  const { data: session } = useSession();
  const enabled =
    hasPermission(session, PERMISSIONS.POS_USE) && hasPermission(session, PERMISSIONS.MENU_VIEW);
  const menu = useApiQuery<MenuItem[]>(queryKeys.menuItems, endpoints.restaurant.menuItems, {
    enabled,
    retry: false,
  });
  const toast = usePrivateToast();
  const t = useTranslate();
  const localName = useLocalName();
  const seen = useRef<Map<string, boolean> | null>(null);
  const selfId = session?.seller?.id ?? null;

  useEffect(() => {
    if (!enabled || !menu.data) return;
    const freshIds = new Set(freshlyMarkedOut(seen.current, menu.data, selfId));
    seen.current = new Map(menu.data.map((item) => [item.id, item.isAvailable]));
    for (const item of menu.data) {
      if (!freshIds.has(item.id)) continue;
      toast.info(
        t((m) => m.pos.menuItemOutToast, {
          item: localName({ nameAr: item.nameAr, nameEn: item.nameEn }),
        }),
        undefined,
        { durationMs: null },
      );
    }
  }, [enabled, localName, menu.data, selfId, t, toast]);

  return null;
}
